import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  useEffect(() => {
    let active = true;
    const token = window.localStorage.getItem("mostash_token");
    if (!token) {
      queueMicrotask(() => { if (active) setUser(false); });
      return undefined;
    }
    api
      .get("/auth/me")
      .then((res) => {
        if (!active) return;
        queueMicrotask(() => setUser(res.data));
      })
      .catch(() => {
        if (!active) return;
        window.localStorage.removeItem("mostash_token");
        queueMicrotask(() => setUser(false));
      });
    return () => { active = false; };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      window.localStorage.setItem("mostash_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail, "Login failed") };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    window.localStorage.removeItem("mostash_token");
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
